// ink-component
import React, { ReactNode, useEffect } from 'react';

import { ThemeProvider } from 'styled-components';
import getGlobalProps from '../../helpers/constants/global-props';
import isDevelopmentEnvironment from '../../helpers/isDevelopmentEnvironment';
import { GridWrapper, ColumnWrapper, RowWrapper } from './styles';
import useWindowWidth from '../../hooks/useWindowWidth';

export interface GridProps {
  /** Any number of Rows */
  children: ReactNode;
  /** In `isMobile` and `isTabletPortrait`, a compact grid will:
   * 1. Remove the offset from the grid itself;
   * 2. Remove the gutter from the first level of row+columns.
   * Objective: maximize real state usage on small devices.
   */
  isCompact?: boolean;
  /** ⚠️ Only use if necessary. Removes the 2000px max-width. */
  isFullWidth?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Contains rows to create page structures
 * @group Grid
 * @categories layout
 * @aka container
 */
export function Grid({ children, isCompact, isFullWidth = false, ...otherProps }: GridProps) {
  useEffect(() => {
    if (isDevelopmentEnvironment()) {
      const condition =
        document.querySelectorAll(`${RowWrapper} ${GridWrapper}, ${ColumnWrapper} ${GridWrapper}`).length > 0;
      const message =
        'Nesting error: Don`t use Grids inside Rows or Columns. ' +
        'Read more: https://ink.carta.com/components/Column';

      if (condition) {
        console.warn(message);
      }
    }
  }, []);

  const { isMobile, isTabletPortrait } = useWindowWidth();

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        gridCompact: isCompact,
        gridSmall: isMobile || isTabletPortrait,
        isFullWidth,
      })}
    >
      <GridWrapper data-testid={otherProps['data-testid']} {...globalProps}>
        {children}
      </GridWrapper>
    </ThemeProvider>
  );
}

Grid.defaultProps = {
  children: undefined,
  isCompact: false,
  'data-testid': undefined,
};

Grid.displayName = 'Grid';

export default Grid;
